import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { UpdateWhatsApp } from '@/types/orderUpdates';
import { WhatsAppBubble } from './WhatsAppBubble';
import { formatUpdateDay } from './updatesFormat';
import { toChatDays } from './whatsappThread';

interface WhatsAppChatProps {
  messages: UpdateWhatsApp[];
}

/** The WhatsApp filter: the conversation as a chat, oldest → newest, opened at the bottom. */
export function WhatsAppChat({ messages }: WhatsAppChatProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const days = useMemo(() => toChatDays(messages), [messages]);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [days]);

  return (
    <div
      ref={scroller}
      data-testid='wa-thread'
      className='max-h-[560px] space-y-3 overflow-y-auto rounded-xl bg-sd-muted/30 p-3 sm:p-4'
    >
      {days.map((day) => (
        <div key={day.key} className='flex flex-col gap-3'>
          <div role='separator' className='flex items-center gap-2'>
            <span className='h-px flex-1 bg-sd-border' />
            <span className='rounded-full bg-sd-card px-2.5 py-0.5 text-[11px] font-medium text-sd-muted-foreground'>
              {formatUpdateDay(day.at, lang)}
            </span>
            <span className='h-px flex-1 bg-sd-border' />
          </div>
          {day.messages.map((message, i) => (
            <WhatsAppBubble key={`${message.id}-${i}`} message={message} />
          ))}
        </div>
      ))}
    </div>
  );
}
